import { Vector3, SlimeState, WobbleSimulator } from '@slimebound/core';
import type { InputManager } from './input-manager.js';

export interface SlimeControllerOptions {
  radius?: number;
  initialPosition?: Vector3;
  maxSpeed?: number;
  hopForce?: number;
  dashForce?: number;
  friction?: number;
  gravity?: number;
  groundY?: number;
}

export class SlimeController {
  public readonly position: Vector3;
  public readonly velocity: Vector3;
  public readonly radius: number;
  public readonly state: SlimeState;
  public readonly wobble: WobbleSimulator;

  public maxSpeed: number;
  public hopForce: number;
  public dashForce: number;
  public friction: number;
  public gravity: number;
  public groundY: number;
  public isGrounded: boolean = true;

  constructor(options: SlimeControllerOptions = {}) {
    this.radius = options.radius ?? 0.9;
    this.position = options.initialPosition ?? new Vector3(0, this.radius, 0);
    this.velocity = new Vector3(0, 0, 0);
    this.maxSpeed = options.maxSpeed ?? 6.5;
    this.hopForce = options.hopForce ?? 8.0;
    this.dashForce = options.dashForce ?? 14.0;
    this.friction = options.friction ?? 9.0;
    this.gravity = options.gravity ?? 22.0;
    this.groundY = options.groundY ?? 0.0;

    this.state = new SlimeState();
    this.wobble = new WobbleSimulator();
  }

  public update(dt: number, input: InputManager, cameraAngle: number = 0): void {
    const moveInput = input.getMoveVector();
    const hasMoveInput = moveInput.x !== 0 || moveInput.z !== 0;

    // Transform input relative to camera orbital yaw
    const cos = Math.cos(cameraAngle);
    const sin = Math.sin(cameraAngle);
    const worldMoveX = moveInput.x * cos + moveInput.z * sin;
    const worldMoveZ = -moveInput.x * sin + moveInput.z * cos;

    // Handle dash energy and speed
    let currentMaxSpeed = this.maxSpeed;
    if (input.isDashing() && hasMoveInput && this.state.consumeEnergy(Math.max(5.0, 25 * dt))) {
      currentMaxSpeed = this.dashForce;
      const currentSpeedSq = this.velocity.x * this.velocity.x + this.velocity.z * this.velocity.z;
      if (currentSpeedSq < this.dashForce * this.dashForce * 0.5) {
        this.velocity.x = worldMoveX * this.dashForce * 0.8;
        this.velocity.z = worldMoveZ * this.dashForce * 0.8;
      }
      this.wobble.applyImpulse(0.15);
    }

    // Horizontal acceleration & friction
    const targetVx = worldMoveX * currentMaxSpeed;
    const targetVz = worldMoveZ * currentMaxSpeed;

    const blend = Math.min(1.0, this.friction * dt);
    this.velocity.x += (targetVx - this.velocity.x) * blend;
    this.velocity.z += (targetVz - this.velocity.z) * blend;

    // Jump handling
    if (input.isJumping() && this.isGrounded) {
      this.velocity.y = this.hopForce;
      this.isGrounded = false;
      this.wobble.applyImpulse(-0.35); // Initial upward stretch
    }

    // Natural rhythmic squish while hopping/sliding on ground
    if (this.isGrounded && hasMoveInput) {
      const speed = Math.hypot(this.velocity.x, this.velocity.z);
      if (Math.random() < 0.08) {
        this.wobble.applyImpulse(speed * 0.015);
      }
    }

    // Gravity
    if (!this.isGrounded) {
      this.velocity.y -= this.gravity * dt;
    }

    // Integrate position
    this.position.x += this.velocity.x * dt;
    this.position.y += this.velocity.y * dt;
    this.position.z += this.velocity.z * dt;

    // Ground collision
    const floorLimit = this.groundY + this.radius;
    if (this.position.y <= floorLimit) {
      if (!this.isGrounded) {
        // Landing impact wobble squish proportional to descent speed
        const impact = Math.min(0.5, Math.abs(this.velocity.y) * 0.04);
        this.wobble.applyImpulse(impact);
      }
      this.position.y = floorLimit;
      this.velocity.y = 0;
      this.isGrounded = true;
    }

    // Update physical wobble spring-damper
    this.wobble.update(dt);
  }
}
